import { z } from 'zod'

export const PendingFulfillmentCartItem = z.object({
  product_id: z.number(),
  product_name: z.string(),
  count: z.number()
})

export const PendingFulfillmentCart = z.object({
  id: z.string().uuid(),
  payment_id: z.string(),
  created: z.string(),
  account: z.object({
    id: z.number(),
    email: z.string()
  }),
  items: z.array(PendingFulfillmentCartItem)
})

export const FulfillmentConfirmResponse = z.object({
  message: z.string(),
  cart_id: z.string(),
  payment_intent: z.string(),
  amount: z.number(),
  currency: z.string()
})

export type PendingFulfillmentCartItem = z.infer<typeof PendingFulfillmentCartItem>
export type PendingFulfillmentCart = z.infer<typeof PendingFulfillmentCart>
export type FulfillmentConfirmResponse = z.infer<typeof FulfillmentConfirmResponse>

// ============================================================================
// Admin Fulfillment Monitoring Types (for /admin/fulfillment/* endpoints)
// ============================================================================

// Enums
export const FulfillmentTaskStatus = z.enum([
  'pending',
  'in_progress',
  'completed',
  'failed',
  'compensated'
])
export type FulfillmentTaskStatus = z.infer<typeof FulfillmentTaskStatus>

export const FulfillmentItemType = z.enum([
  'domain_registration',
  'domain_transfer',
  'internal_transfer',
  'hosting',
  'k8s'
])
export type FulfillmentItemType = z.infer<typeof FulfillmentItemType>

export const StuckDomainItemStatus = z.enum([
  'pending',
  'in_progress',
  'completed',
  'failed'
])
export type StuckDomainItemStatus = z.infer<typeof StuckDomainItemStatus>

// Fulfillment Task - tracks individual fulfillment operations
export const FulfillmentTask = z.object({
  id: z.string().uuid(),
  cart_id: z.string().uuid(),
  account_email: z.string(),
  item_type: FulfillmentItemType,
  item_id: z.string(),
  external_id: z.string().optional(),
  status: FulfillmentTaskStatus,
  step: z.number().int(),
  step_data: z.record(z.string(), z.any()).optional(),
  error: z.string().nullable().optional(),
  attempts: z.number().int(),
  created_at: z.string(),
  updated_at: z.string()
})
export type FulfillmentTask = z.infer<typeof FulfillmentTask>

// Stuck Domain Item - domain item within a stuck cart
export const StuckDomainItem = z.object({
  id: z.number().int(),
  domain_name: z.string(),
  fulfillment_status: StuckDomainItemStatus,
  fulfillment_error: z.string().nullable().optional(),
  external_domain_id: z.number().int().nullable().optional()
})
export type StuckDomainItem = z.infer<typeof StuckDomainItem>

// Stuck Cart - cart that has been in fulfillment status too long
export const StuckCart = z.object({
  id: z.string().uuid(),
  status: z.string(),
  payment_id: z.string(),
  fulfillment_started_at: z.string(),
  stuck_duration: z.string(),
  account_email: z.string(),
  domain_items: z.array(StuckDomainItem).nullable().optional(),
  tasks: z.array(FulfillmentTask).nullable().optional()
})
export type StuckCart = z.infer<typeof StuckCart>

// Response wrapper for GET /admin/fulfillment/stuck
export const StuckCartsResponse = z.object({
  stuck_carts: z.array(StuckCart),
  count: z.number().int(),
  threshold: z.string()
})
export type StuckCartsResponse = z.infer<typeof StuckCartsResponse>

// Response wrapper for GET /admin/fulfillment/tasks
export const FulfillmentTasksResponse = z.object({
  tasks: z.array(FulfillmentTask),
  count: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int()
})
export type FulfillmentTasksResponse = z.infer<typeof FulfillmentTasksResponse>

// ============================================================================
// Admin Orders Types (for GET /admin/carts)
// ============================================================================

// Mirrors backend ent shopping cart status enum (backend/ent/schema/shoppingcart.go)
export const AdminCartStatus = z.enum([
  'started',
  'open',
  'payment_processing',
  'payment_failed',
  'payment_canceled',
  'pending_fulfillment',
  'partial_fulfillment',
  'fulfillment',
  'done',
  'refunded'
])
export type AdminCartStatus = z.infer<typeof AdminCartStatus>

export const AdminCartItem = z.object({
  product_id: z.number().int(),
  product_slug: z.string().optional(),
  product_name: z.string().optional(),
  count: z.number().int(),
  billing_freq: z.string().optional(),
  domain: z.string().optional()
})
export type AdminCartItem = z.infer<typeof AdminCartItem>

export const AdminCart = z.object({
  id: z.string().uuid(),
  status: AdminCartStatus,
  created: z.string(),
  updated: z.string(),
  date_paid: z.string().nullable().optional(),
  refunded_at: z.string().nullable().optional(),
  refund_reason: z.string().optional(),
  payment_id: z.string().optional(),
  account: z.object({
    id: z.number(),
    email: z.string()
  }),
  items: z.array(AdminCartItem)
})
export type AdminCart = z.infer<typeof AdminCart>

// Response wrapper for GET /admin/carts
export const AdminCartsResponse = z.object({
  carts: z.array(AdminCart),
  count: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int()
})
export type AdminCartsResponse = z.infer<typeof AdminCartsResponse>

// ============================================================================
// Admin Webhook Monitoring Types (for /admin/webhook/* endpoints)
// ============================================================================

export const WebhookEventStatus = z.enum([
  'pending',
  'processing',
  'completed',
  'failed',
  'skipped'
])
export type WebhookEventStatus = z.infer<typeof WebhookEventStatus>

// Webhook Event - Stripe webhook event record
export const WebhookEvent = z.object({
  id: z.number().int(),
  event_id: z.string(), // Stripe event ID
  event_type: z.string(), // Stripe event type (e.g., payment_intent.succeeded)
  status: WebhookEventStatus,
  attempts: z.number().int(),
  error: z.string().nullable().optional(),
  next_retry_at: z.string().nullable().optional(),
  received_at: z.string(),
  processed_at: z.string().nullable().optional()
})
export type WebhookEvent = z.infer<typeof WebhookEvent>

// Response wrapper for GET /admin/webhook/failed
export const WebhookEventsResponse = z.object({
  events: z.array(WebhookEvent),
  count: z.number().int(),
  status: z.string(),
  limit: z.number().int(),
  offset: z.number().int()
})
export type WebhookEventsResponse = z.infer<typeof WebhookEventsResponse>

// Response for POST /admin/webhook/{id}/retry
export const WebhookRetryResponse = z.object({
  message: z.string(),
  event_id: z.number().int()
})
export type WebhookRetryResponse = z.infer<typeof WebhookRetryResponse>

// Response for POST /admin/subscription/{id}/provision-stripe
export const ProvisionStripeResponse = z.object({
  message: z.string(),
  subscription_id: z.number().int(),
  stripe_subscription_id: z.string()
})
export type ProvisionStripeResponse = z.infer<typeof ProvisionStripeResponse>
